/**
 * @fileoverview Hook exposing collapsed product group metadata from dashboard context.
 * @module shared/hooks/useProductGroupsMetadata
 */

import { useMemo } from 'react'
import { useDashboardData } from './dashboard/useDashboardData'
import type { StatisticsDimensionGroup } from '@/lib/statisticsService'

/**
 * System variant SKU keys mapped to their placeholder image paths.
 */
const SYSTEM_VARIANT_IMAGES: Record<string, string> = {
  __unmapped__: '/unassigned-product.webp',
  general: '/site-wide-ad-spend.webp',
}

/**
 * Checks if a group represents a system variant and returns the appropriate image.
 * System variants have a single member that matches a known system SKU key.
 */
function getSystemVariantImage(group: StatisticsDimensionGroup): string | null {
  if (group.members.length !== 1) return null
  const memberKey = group.members[0]
  if (!memberKey) return null
  return SYSTEM_VARIANT_IMAGES[memberKey] ?? null
}

/**
 * Result shape for the product groups metadata hook.
 */
export interface UseProductGroupsMetadataResult {
  /** Map of group slug -> metadata returned by the backend */
  productGroups: Map<string, StatisticsDimensionGroup>
  /** Lookup helper returning metadata for a given product key */
  getGroup: (key: string | null | undefined) => StatisticsDimensionGroup | undefined
  /** Predicate indicating whether the provided key references a group */
  isGroupKey: (key: string | null | undefined) => boolean
}

/**
 * Provides collapsed product group metadata resolved from dashboard statistics queries.
 *
 * @returns Helpers and cached metadata for product groups.
 * @example
 * ```tsx
 * const { getGroup, isGroupKey } = useProductGroupsMetadata()
 * const group = getGroup('grp:luna-collection')
 * if (group) {
 *   console.log(group.members)
 * }
 * ```
 */
export function useProductGroupsMetadata(): UseProductGroupsMetadataResult {
  const { productGroups } = useDashboardData()

  return useMemo(() => {
    const getGroup = (key: string | null | undefined): StatisticsDimensionGroup | undefined => {
      if (!key) return undefined
      const group = productGroups.get(key)
      if (!group) return undefined
      
      // Enrich with system variant image if applicable and no image already set
      if (!group.imageUrl) {
        const systemImage = getSystemVariantImage(group)
        if (systemImage) {
          return { ...group, imageUrl: systemImage }
        }
      }
      return group
    }

    const isGroupKey = (key: string | null | undefined): boolean => Boolean(getGroup(key))

    return {
      productGroups,
      getGroup,
      isGroupKey,
    }
  }, [productGroups])
}
